import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TREADMILL_HAND_SHAPES } from "../../data/treadmill/handShapes";
import {
  formatTreadmillTimeShort,
  loadTreadmillLeaderboard,
  TREADMILL_LEADERBOARD_DISPLAY_LIMIT,
  upsertTreadmillLeaderboard,
} from "../../utils/treadmillLeaderboard";
import { subscribeGlobalTreadmillLeaderboard, submitTreadmillPersonalBest } from "../../utils/treadmillLeaderboardFirestore";

const CORRECT_PAUSE_MS = 780;
const TRY_AGAIN_HINT_MS = 1700;

const SUIT_KEYS = ["S", "H", "D", "C"];

const SUIT_META = {
  S: { glyph: "♠", ariaSuit: "Spades", isRed: false },
  H: { glyph: "♥", ariaSuit: "Hearts", isRed: true },
  D: { glyph: "♦", ariaSuit: "Diamonds", isRed: true },
  C: { glyph: "♣", ariaSuit: "Clubs", isRed: false },
};

function parseShape(shapeStr) {
  const s = shapeStr.replace(/\s+/g, "");
  if (s.length !== 4 || !/^\d{4}$/.test(s)) return null;
  const d = s.split("").map((c) => parseInt(c, 10));
  const sum = d[0] + d[1] + d[2] + d[3];
  if (sum !== 13) return null;
  return { s: d[0], h: d[1], d: d[2], c: d[3], raw: s };
}

/** Each problem: random shape and random hidden suit (no fixed cycle). */
function pickRandomRound(shapesLength) {
  const shapeIdx = shapesLength ? Math.floor(Math.random() * shapesLength) : 0;
  const hiddenSuit = SUIT_KEYS[Math.floor(Math.random() * SUIT_KEYS.length)];
  return {
    shapeIdx,
    hiddenSuit,
    columnOrder: buildColumnOrderHiddenLast(hiddenSuit),
  };
}

/** Three visible suits in random order, then hidden suit (vacant box always on the right). */
function buildColumnOrderHiddenLast(hiddenSuit) {
  const visible = SUIT_KEYS.filter((k) => k !== hiddenSuit);
  for (let i = visible.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [visible[i], visible[j]] = [visible[j], visible[i]];
  }
  return [...visible, hiddenSuit];
}

function countForSuit(shape, suitKey) {
  const k = suitKey.toLowerCase();
  return shape[k];
}

export default function HandShapeMissingClubTrainer({
  belowLeaderboardSlot,
  canRecordLeaderboard = false,
  uid = "",
  treadmillUnlimited = true,
  /** When true, show a read-only preview (same pattern as Building blocks / Opponent shape). */
  lockedPreview = false,
  previewNote,
  dailyInteractionBlocked = false,
  dailyFreeRemaining = Infinity,
  onDailyRoundConsumed,
  onSubscribeClick,
  /** Called each time the member reaches 10 correct in a row. */
  onTenInARow,
  /** Called once a session when the member has 10 right in total (Game Plan daily, Paul 20 Oct 2026). */
  onTenRight,
}) {
  const shapes = useMemo(() => TREADMILL_HAND_SHAPES.map(parseShape).filter(Boolean), []);
  const hiddenInputRef = useRef(null);
  const aliasInputRef = useRef(null);
  const advanceTimerRef = useRef(null);
  const tryAgainTimerRef = useRef(null);
  const streakRef = useRef(0);
  const streakStartMsRef = useRef(null);
  const rightTotalRef = useRef(0);

  /** Mirrors streakRef so the live timer can re-render. */
  const [streakCount, setStreakCount] = useState(0);
  /** Bumped on an interval while a streak is active; drives elapsed-time display. */
  const [streakTimerTick, setStreakTimerTick] = useState(0);

  const [round, setRound] = useState(() => pickRandomRound(shapes.length));

  const { shapeIdx, hiddenSuit, columnOrder } = round;
  const shape = shapes[shapeIdx];

  const [hiddenInput, setHiddenInput] = useState("");
  const [showSuccessTick, setShowSuccessTick] = useState(false);
  const [showTryAgain, setShowTryAgain] = useState(false);

  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardStatus, setLeaderboardStatus] = useState("loading");
  /** True when Firestore listener failed; we show scores from localStorage instead. */
  const [leaderboardUsesLocalFallback, setLeaderboardUsesLocalFallback] = useState(false);
  const [pendingStreakRecord, setPendingStreakRecord] = useState(null);
  const [aliasDraft, setAliasDraft] = useState("");
  const [streakSaveError, setStreakSaveError] = useState("");
  const [savingStreak, setSavingStreak] = useState(false);

  useEffect(() => {
    const unsub = subscribeGlobalTreadmillLeaderboard((rows, err) => {
      if (err) {
        const localRows = loadTreadmillLeaderboard().map((e) => ({
          uid: "",
          alias: e.alias,
          timeMs: e.timeMs,
        }));
        setLeaderboard(localRows);
        setLeaderboardStatus("ready");
        setLeaderboardUsesLocalFallback(true);
        return;
      }
      setLeaderboardUsesLocalFallback(false);
      setLeaderboard(rows);
      setLeaderboardStatus("ready");
    });
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, []);

  const expectedHidden = shape ? countForSuit(shape, hiddenSuit) : 0;
  const hiddenMaxLen = expectedHidden > 9 ? 2 : 1;

  const clearTimers = useCallback(() => {
    if (advanceTimerRef.current != null) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    if (tryAgainTimerRef.current != null) {
      window.clearTimeout(tryAgainTimerRef.current);
      tryAgainTimerRef.current = null;
    }
  }, []);

  const advanceAfterSuccess = useCallback(() => {
    clearTimers();
    setShowSuccessTick(false);
    setShowTryAgain(false);
    setHiddenInput("");
    setRound(() => pickRandomRound(shapes.length));
  }, [clearTimers, shapes.length]);

  const resetStreak = useCallback(() => {
    streakRef.current = 0;
    streakStartMsRef.current = null;
    setStreakCount(0);
  }, []);

  const finishStreakModalAndAdvance = useCallback(() => {
    setPendingStreakRecord(null);
    setAliasDraft("");
    resetStreak();
    advanceAfterSuccess();
  }, [advanceAfterSuccess, resetStreak]);

  const saveAliasAndFinish = useCallback(() => {
    const name = aliasDraft.trim();
    if (!name || !pendingStreakRecord) {
      finishStreakModalAndAdvance();
      return;
    }
    if (!canRecordLeaderboard || !uid) {
      finishStreakModalAndAdvance();
      return;
    }
    setSavingStreak(true);
    setStreakSaveError("");
    submitTreadmillPersonalBest(uid, name, pendingStreakRecord.timeMs)
      .then(() => {
        upsertTreadmillLeaderboard(loadTreadmillLeaderboard(), name, pendingStreakRecord.timeMs);
        finishStreakModalAndAdvance();
      })
      .catch(() => {
        upsertTreadmillLeaderboard(loadTreadmillLeaderboard(), name, pendingStreakRecord.timeMs);
        setLeaderboard(
          loadTreadmillLeaderboard().map((e) => ({
            uid: "",
            alias: e.alias,
            timeMs: e.timeMs,
          }))
        );
        setLeaderboardUsesLocalFallback(true);
        finishStreakModalAndAdvance();
      })
      .finally(() => {
        setSavingStreak(false);
      });
  }, [aliasDraft, canRecordLeaderboard, finishStreakModalAndAdvance, pendingStreakRecord, uid]);

  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  useEffect(() => {
    if (pendingStreakRecord) setStreakSaveError("");
  }, [pendingStreakRecord]);

  useEffect(() => {
    if (lockedPreview) return undefined;
    if (pendingStreakRecord) {
      const t = window.setTimeout(() => aliasInputRef.current?.focus?.({ preventScroll: true }), 60);
      return () => window.clearTimeout(t);
    }
    const t = window.setTimeout(() => {
      const el = hiddenInputRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      requestAnimationFrame(() => {
        el.focus({ preventScroll: true });
      });
    }, 0);
    return () => window.clearTimeout(t);
  }, [lockedPreview, shapeIdx, hiddenSuit, columnOrder.join(""), pendingStreakRecord]);

  useEffect(() => {
    if (streakCount === 0 || pendingStreakRecord) return undefined;
    const id = window.setInterval(() => {
      setStreakTimerTick((n) => n + 1);
    }, 100);
    return () => window.clearInterval(id);
  }, [streakCount, pendingStreakRecord]);

  useEffect(() => {
    if (!pendingStreakRecord) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        finishStreakModalAndAdvance();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingStreakRecord, finishStreakModalAndAdvance]);

  const reportDailyRound = useCallback(() => {
    if (uid && !treadmillUnlimited && typeof onDailyRoundConsumed === "function") {
      onDailyRoundConsumed();
    }
  }, [onDailyRoundConsumed, treadmillUnlimited, uid]);

  const handleHiddenChange = useCallback(
    (raw) => {
      if (lockedPreview) return;
      if (dailyInteractionBlocked) return;
      if (showSuccessTick || pendingStreakRecord) return;
      const cleaned = String(raw ?? "")
        .replace(/[^0-9]/g, "")
        .slice(0, hiddenMaxLen);

      if (cleaned.length < hiddenMaxLen) {
        setHiddenInput(cleaned);
        setShowTryAgain(false);
        return;
      }

      const n = Number(cleaned, 10);
      if (Number.isNaN(n)) {
        setHiddenInput(cleaned);
        return;
      }

      if (n === expectedHidden) {
        setHiddenInput(cleaned);
        setShowSuccessTick(true);
        clearTimers();

        rightTotalRef.current += 1;
        if (rightTotalRef.current === 10) {
          try { if (typeof onTenRight === "function") onTenRight(); } catch (e) { console.error("onTenRight failed:", e); }
        }

        streakRef.current += 1;
        const nextStreak = streakRef.current;
        setStreakCount(nextStreak);
        if (nextStreak === 1) {
          streakStartMsRef.current = Date.now();
        }

        if (nextStreak === 10 && streakStartMsRef.current != null) {
          try { if (typeof onTenInARow === "function") onTenInARow(); } catch (e) { console.error("onTenInARow failed:", e); }
          if (canRecordLeaderboard) {
            const timeMs = Date.now() - streakStartMsRef.current;
            advanceTimerRef.current = window.setTimeout(() => {
              advanceTimerRef.current = null;
              reportDailyRound();
              setShowSuccessTick(false);
              setPendingStreakRecord({ timeMs });
            }, CORRECT_PAUSE_MS);
            return;
          }
          resetStreak();
          advanceTimerRef.current = window.setTimeout(() => {
            advanceTimerRef.current = null;
            reportDailyRound();
            advanceAfterSuccess();
          }, CORRECT_PAUSE_MS);
          return;
        }

        advanceTimerRef.current = window.setTimeout(() => {
          advanceTimerRef.current = null;
          reportDailyRound();
          advanceAfterSuccess();
        }, CORRECT_PAUSE_MS);
        return;
      }

      resetStreak();
      setHiddenInput("");
      setShowTryAgain(true);
      clearTimers();
      tryAgainTimerRef.current = window.setTimeout(() => {
        tryAgainTimerRef.current = null;
        setShowTryAgain(false);
      }, TRY_AGAIN_HINT_MS);
      window.setTimeout(() => {
        hiddenInputRef.current?.focus?.({ preventScroll: true });
      }, 0);
    },
    [
      advanceAfterSuccess,
      clearTimers,
      dailyInteractionBlocked,
      expectedHidden,
      hiddenMaxLen,
      lockedPreview,
      canRecordLeaderboard,
      onTenInARow,
      onTenRight,
      pendingStreakRecord,
      reportDailyRound,
      resetStreak,
      showSuccessTick,
    ]
  );

  const streakElapsedMs = useMemo(() => {
    if (streakCount === 0 || pendingStreakRecord || streakStartMsRef.current == null) return null;
    void streakTimerTick;
    return Date.now() - streakStartMsRef.current;
  }, [streakCount, pendingStreakRecord, streakTimerTick]);

  const showStreakHud = streakCount > 0 && !pendingStreakRecord;

  if (!shape) {
    return (
      <div className="ct-questionText" style={{ fontSize: "var(--text-base)" }}>
        No valid shapes are configured. Add entries to the hand shape list.
      </div>
    );
  }

  const rows = columnOrder.map((suitKey) => {
    const meta = SUIT_META[suitKey];
    const locked = suitKey !== hiddenSuit;
    const count = countForSuit(shape, suitKey);
    return {
      suitKey,
      glyph: meta.glyph,
      ariaSuit: meta.ariaSuit,
      isRed: meta.isRed,
      locked,
      displayValue: locked ? String(count) : lockedPreview ? "?" : hiddenInput,
    };
  });

  const hiddenAriaSuit = SUIT_META[hiddenSuit]?.ariaSuit || "suit";
  const inputLocked = showSuccessTick || !!pendingStreakRecord || lockedPreview;
  const previewShellClass = lockedPreview ? "tm-toolPreview tm-toolPreview--locked" : "";

  return (
    <div className={`tm-handShape ${previewShellClass}`} aria-live="polite">
      {lockedPreview ? (
        <p className="tm-toolPreview-note">{previewNote || "Preview only. Sign in or create an account below to play."}</p>
      ) : null}
      {dailyInteractionBlocked && !treadmillUnlimited ? (
        <div className="tm-handShape-dailyWall" role="status">
          <p className="tm-toolPreview-note">
            You&apos;ve used today&apos;s 4 free correct answers on this drill. Subscribe for unlimited access.
          </p>
          {typeof onSubscribeClick === "function" ? (
            <button type="button" className="ct-btn ct-btn--secondary" onClick={onSubscribeClick}>
              View membership
            </button>
          ) : null}
        </div>
      ) : null}
      {!dailyInteractionBlocked &&
      uid &&
      !treadmillUnlimited &&
      Number.isFinite(dailyFreeRemaining) ? (
        <p className="tm-handShape-dailyHint" aria-live="polite">
          Free today: {dailyFreeRemaining} of 4 free correct {dailyFreeRemaining === 1 ? "answer" : "answers"} left on
          this drill.
        </p>
      ) : null}
      {!dailyInteractionBlocked ? (
        <>
          <div className="tm-handShape-introRow">
            <div className="ct-questionText tm-handShape-intro">Fill in the missing shape</div>
            {showStreakHud ? (
              <div
                className="tm-handShape-streakHud"
                role="timer"
                aria-label={`Current streak ${streakCount} of 10, elapsed ${formatTreadmillTimeShort(
                  streakElapsedMs ?? 0
                )}`}
              >
                <span className="tm-handShape-streakHud-count">{streakCount}/10</span>
                <span className="tm-handShape-streakHud-time" aria-hidden="true">
                  {formatTreadmillTimeShort(streakElapsedMs ?? 0)}
                </span>
              </div>
            ) : null}
          </div>
          {!lockedPreview && !showStreakHud ? (
            <p className="tm-handShape-preTimerHint">Don&apos;t use addition, just pattern recognition.</p>
          ) : null}

          <div
            className={`ct-shapeRow tm-shapeRow ${pendingStreakRecord ? "tm-handShape--blocked" : ""}`}
            role="group"
            aria-label="Hand shape lengths by suit"
          >
        {rows.map((row) => (
          <div
            key={row.suitKey}
            className="ct-distSeat tm-distSeat"
            {...(row.locked ? { "aria-label": `${row.ariaSuit} length ${row.displayValue}` } : {})}
          >
            <div className="ct-distLabel tm-suitDistLabel" aria-hidden="true">
              <span className={`ct-suitSym tm-suitGlyph ${row.isRed ? "ct-suitSym--red" : "ct-suitSym--black"}`}>
                {row.glyph}
              </span>
            </div>
            <div
              className={`ct-numBox ct-numBox--dist ${row.locked || lockedPreview ? "ct-numBox--locked" : ""} ${
                showSuccessTick && !row.locked ? "tm-numBox--successPulse" : ""
              }`}
              {...(!row.locked && !lockedPreview
                ? {
                    onPointerDown: (e) => {
                      if (inputLocked) return;
                      const el = hiddenInputRef.current;
                      if (!el) return;
                      if (e.pointerType === "mouse") {
                        e.preventDefault();
                      }
                      el.focus({ preventScroll: true });
                      if (e.pointerType === "mouse") {
                        el.select();
                      }
                    },
                  }
                : {})}
            >
              <div className="ct-numBoxValue ct-numBoxValue--dist" aria-hidden="true">
                {row.displayValue}
              </div>
              {!row.locked && !lockedPreview ? (
                <input
                  ref={hiddenInputRef}
                  className="ct-numBoxInput ct-numBoxInput--hidden tm-handShapeDigitInput"
                  type="text"
                  inputMode="numeric"
                  enterKeyHint="done"
                  pattern="[0-9]*"
                  maxLength={hiddenMaxLen}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  aria-label={`Enter ${hiddenAriaSuit} length`}
                  value={hiddenInput}
                  onChange={(e) => handleHiddenChange(e.target.value)}
                  onFocus={(e) => {
                    if (!showSuccessTick && !pendingStreakRecord) e.target.select();
                  }}
                  readOnly={showSuccessTick}
                  disabled={!!pendingStreakRecord}
                />
              ) : null}
            </div>
          </div>
        ))}
          </div>

          <div className="tm-handShape-feedbackSlot" aria-live="assertive">
            <div className="tm-handShape-feedbackInner">
              {!pendingStreakRecord && showSuccessTick && (
                <div className="tm-successTick" role="status" aria-label="Correct">
                  <span className="tm-successTickMark" aria-hidden="true">
                    ✓
                  </span>
                </div>
              )}
              {!pendingStreakRecord && showTryAgain && (
                <div className="tm-tryAgainHint" role="status">
                  Try again
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}

      <section className="tm-lbBoard" aria-labelledby="tm-lb-board-title">
        <div className="tm-lbBoard-header">
          <h2 id="tm-lb-board-title" className="tm-lbBoard-title">
            Leaderboard
          </h2>
          <p className="tm-lbBoard-tagline">
            {leaderboardUsesLocalFallback
              ? `Showing this device only (cloud list unavailable). Top ${TREADMILL_LEADERBOARD_DISPLAY_LIMIT} · 10 in a row · one best time per name here.`
              : canRecordLeaderboard
                ? `Global top ${TREADMILL_LEADERBOARD_DISPLAY_LIMIT} · 10 correct in a row · one best time per signed-in player`
                : `Sign in to record a time. Global top ${TREADMILL_LEADERBOARD_DISPLAY_LIMIT} · 10 correct in a row · one best time per player`}
          </p>
        </div>
        <div className="tm-lbTableWrap">
          <table className="tm-lbTable">
            <thead>
              <tr>
                <th scope="col" className="tm-lbTh tm-lbTh--rank">
                  #
                </th>
                <th scope="col" className="tm-lbTh">
                  Name
                </th>
                <th scope="col" className="tm-lbTh tm-lbTh--time">
                  Time
                </th>
              </tr>
            </thead>
            <tbody>
              {leaderboardStatus === "loading" ? (
                <tr>
                  <td colSpan={3} className="tm-lbTd tm-lbTd--empty">
                    Loading leaderboard…
                  </td>
                </tr>
              ) : leaderboard.length === 0 && leaderboardUsesLocalFallback ? (
                <tr>
                  <td colSpan={3} className="tm-lbTd tm-lbTd--empty">
                    No scores saved on this device yet. Hit 10 correct in a row to post a time.
                  </td>
                </tr>
              ) : leaderboard.length === 0 ? (
                <tr>
                  <td colSpan={3} className="tm-lbTd tm-lbTd--empty">
                    {canRecordLeaderboard
                      ? "No scores yet. Hit 10 correct in a row to post a time."
                      : "Sign in to post a time. Practice runs do not add scores while you are signed out."}
                  </td>
                </tr>
              ) : (
                leaderboard.map((e, i) => (
                  <tr
                    key={e.uid ? `cloud-${e.uid}` : `local-${i}-${e.alias}`}
                    className={i < 3 ? `tm-lbTr--place${i + 1}` : undefined}
                  >
                    <td className="tm-lbTd tm-lbTd--rank">{i + 1}</td>
                    <td className="tm-lbTd tm-lbTd--name">{e.alias}</td>
                    <td className="tm-lbTd tm-lbTd--time">{formatTreadmillTimeShort(e.timeMs)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {belowLeaderboardSlot}

      {pendingStreakRecord && (
        <div className="tm-lbOverlay" role="dialog" aria-modal="true" aria-labelledby="tm-lb-title">
          <div className="tm-lbCard">
            <h2 id="tm-lb-title" className="tm-lbTitle">
              10 in a row
            </h2>
            <p className="tm-lbTimeLine">
              Time: <strong>{formatTreadmillTimeShort(pendingStreakRecord.timeMs)}</strong>
            </p>
            <label className="tm-lbLabel" htmlFor="tm-lb-alias">
              {leaderboardUsesLocalFallback
                ? "Name or alias (saved on this device)"
                : "Name or alias (shown on the global leaderboard)"}
            </label>
            <input
              id="tm-lb-alias"
              ref={aliasInputRef}
              className="tm-lbInput"
              type="text"
              maxLength={40}
              autoComplete="nickname"
              value={aliasDraft}
              onChange={(e) => setAliasDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveAliasAndFinish();
                }
              }}
            />
            {streakSaveError ? (
              <div className="tm-lbError" role="alert">
                {streakSaveError}
              </div>
            ) : null}
            <div className="tm-lbActions">
              <button
                type="button"
                className="ct-btn"
                onClick={saveAliasAndFinish}
                disabled={savingStreak}
              >
                {savingStreak ? "Saving…" : "Save to board"}
              </button>
              <button
                type="button"
                className="ct-btn ct-btn--secondary"
                onClick={finishStreakModalAndAdvance}
                disabled={savingStreak}
              >
                Skip
              </button>
            </div>
            <p className="tm-lbHint">
              {`Press Escape to skip. Only your fastest run is stored on your account. If it beats your previous best, you may appear in the global top ${TREADMILL_LEADERBOARD_DISPLAY_LIMIT} above.`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
